import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { X, Settings } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';

export default function CookieBanner() {
  const [showBanner, setShowBanner] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false,
    marketing: false,
  });
  
  useEffect(() => {
    const consent = localStorage.getItem('cookie-consent');
    if (!consent) {
      setShowBanner(true);
    }
  }, []);
  
  const acceptAll = () => {
    const allAccepted = { necessary: true, analytics: true, marketing: true };
    localStorage.setItem('cookie-consent', JSON.stringify(allAccepted));
    setShowBanner(false);
  };
  
  const acceptSelected = () => {
    localStorage.setItem('cookie-consent', JSON.stringify(preferences));
    setShowBanner(false);
  };
  
  const rejectAll = () => {
    const onlyNecessary = { necessary: true, analytics: false, marketing: false };
    localStorage.setItem('cookie-consent', JSON.stringify(onlyNecessary));
    setShowBanner(false);
  };
  
  if (!showBanner) return null;
  
  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-50 lg:bottom-6 lg:left-6 lg:right-auto lg:max-w-md"
      data-testid="cookie-banner"
    >
      <div className="bg-white shadow-2xl p-6 lg:rounded-lg border border-stone-200">
        <div className="flex items-start justify-between mb-4">
          <h3 className="font-heading text-xl text-night">Cookie & Privacy</h3>
          <button
            onClick={rejectAll}
            className="text-stone-400 hover:text-night transition-colors"
            data-testid="cookie-close"
          >
            <X size={20} />
          </button>
        </div>
        
        <p className="font-body text-sm text-stone-600 mb-6">
          Utilizziamo cookie per migliorare la tua esperienza. Puoi accettare tutti i cookie o personalizzare le tue preferenze.
        </p>
        
        <div className="flex flex-wrap gap-3">
          <Button
            onClick={acceptAll}
            className="flex-1 bg-night text-ivory hover:bg-stone-800 rounded-full"
            data-testid="cookie-accept-all"
          >
            Accetta Tutti
          </Button>
          
          <Dialog>
            <DialogTrigger asChild>
              <Button
                variant="outline"
                className="flex-1 border-stone-300 text-night hover:bg-stone-100 rounded-full"
                data-testid="cookie-customize"
              >
                <Settings size={16} className="mr-2" />
                Personalizza
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white">
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl">Preferenze Cookie</DialogTitle>
              </DialogHeader>
              <div className="space-y-6 py-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="font-body font-semibold">Cookie Necessari</Label>
                    <p className="font-body text-xs text-stone-500">Essenziali per il funzionamento del sito</p>
                  </div>
                  <Switch checked disabled />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="font-body font-semibold">Cookie Analitici</Label>
                    <p className="font-body text-xs text-stone-500">Per analizzare l'utilizzo del sito</p>
                  </div>
                  <Switch
                    checked={preferences.analytics}
                    onCheckedChange={(v) => setPreferences({ ...preferences, analytics: v })}
                    data-testid="cookie-switch-analytics"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="font-body font-semibold">Cookie Marketing</Label>
                    <p className="font-body text-xs text-stone-500">Per pubblicità personalizzata</p>
                  </div>
                  <Switch
                    checked={preferences.marketing}
                    onCheckedChange={(v) => setPreferences({ ...preferences, marketing: v })}
                    data-testid="cookie-switch-marketing"
                  />
                </div>
              </div>
              <Button
                onClick={acceptSelected}
                className="w-full bg-night text-ivory hover:bg-stone-800 rounded-full"
                data-testid="cookie-save-preferences"
              >
                Salva Preferenze
              </Button>
            </DialogContent>
          </Dialog>
          
          <Button
            variant="ghost"
            onClick={rejectAll}
            className="w-full text-stone-500 hover:text-night text-sm"
            data-testid="cookie-reject"
          >
            Solo Necessari
          </Button>
        </div>
      </div>
    </div>
  );
}
